import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUserName } from "../utils/getUserName";

export default function GameStart() {
  const navigate = useNavigate();
  const [isPaused, setIsPaused] = useState(false);
  const [loading, setLoading] = useState(0);

const userName = getUserName();

useEffect(() => {
    const handleBlur = () => setIsPaused(true);
    const handleFocus = () => setIsPaused(false);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    const interval = setInterval(() => {
      setLoading(prev => (prev < 100 ? prev + 1 : 100));
    }, 30);

    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[100] bg-[#090a0b] text-white flex flex-col items-center justify-center overflow-y-auto overflow-x-hidden select-none px-4 py-6 md:py-10">
      
      {/* 1. PAUSE OVERLAY */}
      {isPaused && (
        <div className="fixed inset-0 z-[200] bg-[#090a0b]/95 backdrop-blur-2xl flex flex-col items-center justify-center text-center px-6">
          <div className="space-y-4">
            <h2 className="text-3xl md:text-5xl font-black tracking-widest text-blue-500 uppercase">System Paused</h2>
            <p className="text-zinc-200 tracking-widest text-[10px] uppercase font-bold">Return to the tab to continue</p>
            <button 
              onClick={() => setIsPaused(false)}
              className="mt-8 px-10 py-4 border border-blue-500/40 text-white text-[10px] tracking-widest uppercase hover:bg-blue-500/20 transition-all"
            >
              Resume Session
            </button>
          </div>
        </div>
      )}

      {/* 2. BACKGROUND DESIGN */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#1e3a8a_0%,_transparent_70%)] animate-pulse" />
      </div>

      {/* 3. TOP INFO BAR (Responsive Layout) */}
      <div className="absolute top-4 md:top-10 w-full px-4 md:px-12 flex flex-row justify-between items-center opacity-70">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full shadow-[0_0_10px_#3b82f6] animate-pulse" />
          <p className="text-[8px] md:text-[10px] tracking-widest font-black uppercase">Omore Games</p>
        </div>
<div className="flex flex-col items-center justify-center px-3 py-1 rounded-md bg-white/10 border border-white/20">

  <span className="text-[8px] md:text-[10px] tracking-widest text-zinc-300 uppercase font-semibold">
    Player
  </span>

  <span className="text-[12px] md:text-sm font-bold text-white tracking-wide">
    {userName}
  </span>

</div>
        <div className="flex flex-col items-end gap-1">
          <p className="text-[7px] md:text-[9px] tracking-widest text-zinc-200 uppercase font-bold">System Load</p>
          <div className="w-20 md:w-32 h-1 bg-zinc-900 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${loading}%` }} />
          </div>
        </div>
      </div>

      {/* 4. MAIN MENU */}
      <div className="relative z-10 text-center max-w-6xl w-full flex flex-col items-center space-y-8 md:space-y-16 mt-8 md:mt-0">
        
        <header className="space-y-2 md:space-y-4 px-2">
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-black tracking-tighter uppercase  leading-tight">
            Select <span className="text-blue-500 text-glow">Module</span>
          </h1>
          <p className="text-[9px] md:text-xs text-zinc-200 tracking-[0.3em] md:tracking-[0.5em] uppercase max-w-md mx-auto">
            Choose a training level below to begin your sequence
          </p>
        </header>

        {/* GAME CARDS (Responsive Grid) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 w-full px-2 md:px-6">
          {[
            { id: '01', title: 'Reflex Test', desc: 'Test how fast you can click', path: '/game/reflex' },
            { id: '02', title: 'Focus Test', desc: 'Keep your eyes on the target', path: '/game/focus' },
            { id: '03', title: 'Brain Game', desc: 'Remember the patterns', path: '/game/memory' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className="relative p-6 md:p-10 border border-blue-500/30 bg-blue-600/10 text-left hover:bg-blue-600/20 hover:border-blue-500/60 transition-all duration-300 group"
            >
              <span className="text-[9px] md:text-[10px] font-mono text-blue-500/60 block mb-2 md:mb-4 uppercase">Task {item.id}</span>
              <h3 className="text-xl md:text-2xl font-bold tracking-tight uppercase text-white mb-1 md:mb-2">{item.title}</h3>
              <p className="text-[9px] md:text-[10px] tracking-wide text-blue-400/70 uppercase font-medium leading-relaxed">{item.desc}</p>
              
              {/* Corner Accents */}
              <div className="absolute top-0 right-0 w-4 h-4 md:w-6 md:h-6 border-t-2 border-r-2 border-blue-500/40 group-hover:border-blue-500 transition-colors" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-blue-500/20" />
            </button>
          ))}
        </div>

        {/* EXIT BUTTON */}
        <button 
          onClick={() => navigate("/")}
          className="text-[9px] md:text-[10px] tracking-[0.6em] text-red-500 hover:text-red-500 uppercase font-black transition-colors py-4"
        >
          [ Exit Game ]
        </button>
      </div>

      {/* 5. BOTTOM STATUS (Hidden on small mobile for space) */}
      <div className="absolute bottom-6 md:bottom-10 w-full px-6 flex justify-center hidden sm:flex pointer-events-none">
        <p className="text-[8px] md:text-[9px] tracking-[1em] text-zinc-800 uppercase font-black animate-pulse">
          Link: Active • Status: Online
        </p>
      </div>
    </div>
  );
}